import React, { createRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FullscreenLayout } from 'src/components/widgets/fullscreenIframeLayout';
// LE VOCI DEL RAIL PORTANO I GLIFI MATERIAL, quelli del resto del Backoffice: il Composer li usa
// già, e due famiglie di disegni nella stessa barra si leggono come due barre.
import { T } from '../../../../../shared/helpers';
import { useElementSize } from '../../../../../shared/hooks/useElementSize';
import { useSubscriptionData } from '../../../../../shared/hooks/useSubscriptionData';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { useProductConfigurationState } from '../../state';
import HeaderToolbar from '../configurator-components/headerToolbar';
import LeftVerticalToolbar, { PanelCollapseToggle, StepsTools } from '../configurator-components/leftVerticalToolbar';
import CameraEditor from '../configurator-components/scene-editors/cameraEditor';
import LightsEditor from '../configurator-components/scene-editors/lightsEditor';
import MaterialsEditor from '../configurator-components/scene-editors/materialsEditor';
import { commitFinishChoices } from '../configurator-components/scene-editors/MaterialOverlaySimple';
import { materialsWithoutPreview, shootPreviews } from '../configurator-components/scene-editors/materialPreview';
import ObjectEditor from '../configurator-components/scene-editors/objectEditor';
import { VTOProviderSync } from '../configurator-components/scene-editors/vtoEditor';
import TexturesEditor from '../configurator-components/scene-editors/texturesEditor';
import EnvironmentsEditor from '../configurator-components/scene-editors/environmentsEditor';
import LookEditor from '../configurator-components/scene-editors/lookEditor';
import { SHOW_NOT_YET_AVAILABLE } from '../configurator-components/scene-editors/notYetAvailable';
import { TreeHandle } from '../configurator-components/TreeComponent';
import SceneEditorSettingsDialog, { SettingsDialogWindow } from '../configurator-components/sceneEditorSettingsDialog';
import { useMovableHost } from './useMovableHost';

// Relay generated types
import useSaveProductAndScene from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import { Load3DSceneEditor } from '../../../../widgets/preview3D';
import { EditorLoadingCard } from '../configurator-components/editorLoadingCard';
import SceneEditorView from '../../../../widgets/preview3D/sceneEditorView';
import { SceneEditorToolbar } from '../configurator-components/scene-editors/toolbar';
import { Panel, Resizer, StageColumn, Workspace } from '../../../../widgets/workspaceLayout/workspaceLayout';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { getWorkspaceRailWidth, useWorkspacePreferences } from './useWorkspacePreferences';
import { applySelectionHighlight, SelectionHighlight } from './selectionHighlight';
import { useSceneBackground } from './useSceneBackground';
import { useEscapeClosesModals } from '../../../../widgets/hooks/useEscapeToClose';
import {
    CamerasRailIcon as CamerasIcon,
    LightsRailIcon as LightsIcon,
    LookRailIcon as LookIcon,
    MaterialsRailIcon as MaterialsIcon,
    ObjectsRailIcon as ObjectsIcon
} from '../configurator-components/scene-editors/sceneRailIcons';
import { EnvironmentsIcon, SelectedIcon, TexturesIcon } from '../../../../widgets/icons/scene';
import { INSPECTOR_BACKGROUND } from '../configurator-components/treeRowLook';
import { editorPalette, withEditorTheme } from '../configurator-components/editorPalette';
import { InspectorEmptyHead } from '../configurator-components/scene-editors/inspectorShell';
import { SceneSettingsPage, useSceneSettingsRequest } from '../configurator-components/scene-editors/sceneSettingsRequest';
import { sceneFingerprint } from './sceneFingerprint';

interface MeshTreeViewItem {
    id: string;
    name: string;
}

enum Mode {
    Meshes = 'meshes',
    Materials = 'materials',
    Lights = 'lights',
    Camera = 'camera'
}

interface SceneEditorPageState {
    isEnvironmentTextureUploading: boolean;
    isSkyboxTextureUploading: boolean;
    zkModel: any;
    zkScene: any;
    meshes: any[];
    materials: any[];
    lights: any[];
    cameraLocations: any[];
    tasks: any[];
    selectedMeshes: any[];
    selectedMaterials: any[];
    selectedLights: any[];
    selectedCameraLocations: any[];
    filters: {
        meshes: string;
        lights: string;
        materials: string;
    };
    mode: Mode;
    propertiesPanelOpen: boolean;
    loadingPercentage: number;
    texturesLoading: any[];
    showPreview: boolean;
    explodeMode: boolean;
    hasVTOReferenceModels: boolean;
    selectedVTOType: any;
    tryOnSettingsComponent: React.RefObject<any>;
    isAdmin: boolean;
    importDropdownMenuIsOpened: boolean;
    isUploadingDamMaterial: boolean;
    providersMapping: {
        list: Record<string, any>;
    };
    uploadedQueue: any[];
    groupMaterialsDisplayOrder: Map<string, number>;
}

const Container = styled(FullscreenLayout)`
    ${editorPalette}

    align-items: stretch;
    background: var(--editor-chrome);
    z-index: 30;

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const PreviewContainer = styled(Panel)`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    min-height: 0;
    padding: 0;
    /* Niente fondo sotto il modello, ma lo stesso bordo dei pannelli: senza, il palco finiva
       dove finiva la tela e la cucitura coi pannelli non si leggeva. */
    background: transparent;
`;

// L'INSPECTOR NON HA PIÙ UN RAIL SUO, in nessun attracco (round 4, pacchetto B): Environment e
// Post-process sono righe di Look, nel rail di sinistra come le altre liste. Della colonna restano il
// soggetto e, in fondo, il piede col chevron che la chiude.
const InspectorPanel = styled(Panel)`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    /* LA COLONNA È GRIGIA TUTTA (utente, 2026-10-15): anche vuota e nel piede, non solo sotto il soggetto. */
    background: ${INSPECTOR_BACKGROUND};
`;

// Chiusa, della colonna resta il solo piede: il contenuto non si ritaglia, esce — come nel
// pannello di sinistra, e per la stessa ragione (un contenuto stretto a 43px si ridisegna tutto
// per non essere visto).
const InspectorBody = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;

    &[hidden] {
        display: none;
    }
`;

// IL PIEDE DELLA COLONNA, con lo stesso chevron del rail di sinistra (prototipo #28, #61, #88). Sta
// dalla parte da cui la colonna si chiude: verso l'elenco quando l'Inspector gli sta accanto, verso il
// bordo quando sta a destra. Largo quanto il rail, così da chiusa la striscia è quel chevron e basta.
const InspectorFoot = styled.div<{ $side: 'left' | 'right' }>`
    display: flex;
    flex: none;
    justify-content: ${(props) => (props.$side === 'left' ? 'flex-start' : 'flex-end')};
    margin-top: auto;
    padding-bottom: 8px;

    > * {
        display: flex;
        justify-content: center;
        width: var(--w-rail-cur);
    }
`;

// Lo scorrimento non sta qui: ce l'ha il corpo di `InspectorShell`, sotto la sua testata, che
// deve restare ferma mentre le proprietà scorrono.
const InspectorContent = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow: hidden;
`;

// IL POSTO DICE CHE È VUOTO invece di sparire. Stessa forma del mockup: un glifo, una riga di
// prosa, tutto attenuato — non è un errore e non è un comando: è la colonna che aspetta un
// soggetto.
const InspectorEmpty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px 28px;
    text-align: center;
    /* Il grigio chiaro e 12px del prototipo (Appendice A): una colonna che aspetta, non un messaggio. */
    color: var(--editor-faint, ${(props) => props.theme.app.primary});

    p {
        max-width: 34ch;
        margin: 0;
        font-size: 12px;
        line-height: 1.6;
    }

    svg {
        width: 26px;
        height: 26px;
        opacity: 0.5;
    }
`;

const SmallScreenBanner = styled.div`
    display: none;

    @media (max-width: 940px) {
        display: block;
        position: absolute;
        top: 12px;
        left: 12px;
        right: 12px;
        z-index: 20;
    }
`;

const StageEmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.app.primary};
`;

const SceneEditor: React.FC = () => {
    // Esc chiude la finestra in primo piano, finché si lavora qui: vedi `useEscapeToClose`.
    useEscapeClosesModals();
    const { showDialog, closeDialog } = useDialogManager();
    // Hook per ottenere i dati della subscription con additionalFeatures
    const { subscription } = useSubscriptionData();
    const additionalFeatures = subscription?.plan?.additionalFeatures;

    // Refs per mantenere le referenze come nella classe
    // UN RIFERIMENTO PER SEZIONE, e non uno solo per tutte.
    //
    // «Portami a quella cosa» ha un bersaglio diverso a seconda di dove si va: una mesh
    // nell'albero degli oggetti, un materiale nella sua libreria. Con un riferimento unico vince
    // l'ultima sezione che si è montata — e siccome la libreria vive in un portale che sta in piedi
    // anche quando non la si guarda, vinceva lei: selezionare una mesh nel palco chiedeva a un
    // materiale di aprirsi, non lo trovava, e non apriva niente.
    //
    // Chi chiama dice già a quale sezione va (`revealTarget(tool, id)`): basta che il riferimento
    // lo scelga in base a quello.
    const treeRef = useRef<TreeHandle>(null);
    const materialsRef = useRef<TreeHandle>(null);
    const meshesScrollbarsRef = useRef<any>(null);
    const materialsScrollbarsRef = useRef<any>(null);
    const workspaceRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    // Le larghezze si salvano in frazioni del workspace: per convertirle serve la sua misura.
    const [measureWorkspace, workspaceSize] = useElementSize(workspaceRef);
    const { preferences, limits, defaults, setPreference, setPanelSize, resetPanelSizes, toggleCollapsed } = useWorkspacePreferences(workspaceSize);
    // L'ALONE È UNA PREFERENZA COME IL TEMA, e sta nello stesso documento sul server: il motore lo
    // vuole come oggetto, il documento lo tiene come due voci di testo.
    const selectionHighlight = useMemo<SelectionHighlight>(
        () => ({ enabled: preferences.selectionHighlight === 'on', color: preferences.highlightColor }),
        [preferences.highlightColor, preferences.selectionHighlight]
    );

    const updateSelectionHighlight = useCallback(
        (changes: Partial<SelectionHighlight>) => {
            if (changes.enabled !== undefined) setPreference('selectionHighlight', changes.enabled ? 'on' : 'off');
            if (changes.color !== undefined) setPreference('highlightColor', changes.color);
        },
        [setPreference]
    );
    const [inspectorContent, setInspectorContent] = useState<React.ReactNode | null>(null);

    // Hook per il salvataggio del prodotto e scena
    const [saveProductAndScene, isSaving] = useSaveProductAndScene();

    // Variabili di istanza convertite in useRef per persistenza
    const lastTasksLengthRef = useRef<number>(0);
    const treeItemsRef = useRef<MeshTreeViewItem[]>([]);
    const creatingGroupParentRef = useRef<any | null>(null);
    const sidebarWidthRef = useRef<number>(0);
    const sceneReadyRef = useRef<boolean>(false);
    const currentTaskRef = useRef<any | null>(null);

    // Stato iniziale convertito da this.state
    const [state, setState] = useState<SceneEditorPageState>({
        isEnvironmentTextureUploading: false,
        isSkyboxTextureUploading: false,
        zkModel: null,
        zkScene: undefined,
        meshes: [],
        materials: [],
        lights: [],
        cameraLocations: [],
        tasks: [],
        selectedMeshes: [],
        selectedMaterials: [],
        selectedLights: [],
        selectedCameraLocations: [],
        filters: { meshes: '', lights: '', materials: '' },
        mode: Mode.Meshes,
        propertiesPanelOpen: false,
        loadingPercentage: 0,
        texturesLoading: [],
        showPreview: false,
        explodeMode: false,
        hasVTOReferenceModels: false,
        selectedVTOType: undefined,
        tryOnSettingsComponent: createRef(),
        isAdmin: localStorage.getItem('is_admin') ? true : false,
        importDropdownMenuIsOpened: false,
        isUploadingDamMaterial: false,
        providersMapping: { list: {} },
        uploadedQueue: [],
        groupMaterialsDisplayOrder: new Map<string, number>()
    });

    // Stato per overlay loading (convertito da proprietà di classe)
    const { dispatch, state: productState, unsavedChanges } = useProductConfigurationState();
    const [zkProduct, zkScene, sceneEditor, activeTool, setActiveTool] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneEditor,
        x.sceneEditorActiveTool,
        x.setSceneEditorActiveTool
    ]);
    const [isSceneEditorReady, setIsSceneEditorReady] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // IL FONDALE È UN MODO DI GUARDARE, e il suo interruttore sta sul palco — «Preview mode», in basso
    // a destra. Lo stato però vive qui e non nella barra, perché non ha un padrone solo: anche
    // Environment, in Look, lo accende aprendosi, e uno stato tenuto dalla barra la sezione non saprebbe
    // raggiungerlo.
    const [backgroundOn, toggleBackground] = useSceneBackground(sceneEditor, selectionHighlight);

    const markSceneDirty = useCallback(() => {
        if (sceneReadyRef.current) setHasUnsavedChanges(true);
    }, []);

    // L'IMPRONTA DELL'ULTIMA SCENA SALVATA (sceneFingerprint): uscendo si confronta, così una modifica che nessun
    // campo ha segnalato (l'occhio, il trascinamento di un numero, il gizmo) non si perde senza avviso.
    const savedFingerprintRef = useRef('');
    const checkUnsavedSceneChanges = useCallback(() => {
        if (!sceneReadyRef.current || !savedFingerprintRef.current) return false;
        const changed = sceneFingerprint(sceneEditor, zkScene) !== savedFingerprintRef.current;
        if (changed) setHasUnsavedChanges(true);
        return changed;
    }, [sceneEditor, zkScene]);

    // Dopo un clic o un tasto, un controllo con un attimo di ritardo: la testata dice «Unsaved changes» subito, non solo
    // uscendo. Finché la scena risulta già da salvare non serve.
    const pendingCheckRef = useRef<number | null>(null);
    const scheduleSceneCheck = useCallback(() => {
        if (hasUnsavedChanges) return;
        if (pendingCheckRef.current) window.clearTimeout(pendingCheckRef.current);
        pendingCheckRef.current = window.setTimeout(() => {
            pendingCheckRef.current = null;
            checkUnsavedSceneChanges();
        }, 400);
    }, [hasUnsavedChanges, checkUnsavedSceneChanges]);
    useEffect(() => () => void (pendingCheckRef.current && window.clearTimeout(pendingCheckRef.current)), []);

    // CERCARE NON È MODIFICARE. Le barre di ricerca sono campi come gli altri e stanno dentro
    // questo ascolto: senza l'eccezione, filtrare una lista o cercare un parametro lasciava la
    // scena «da salvare», e un avviso di modifiche non salvate che compare quando non si è
    // toccato niente smette di voler dire qualcosa. Chi cambia solo CIÒ CHE SI VEDE lo dichiara
    // con `data-view-only`, e lo dichiara chi lo sa: il campo stesso.
    const sceneMayHaveChanged = useCallback(
        (event: React.SyntheticEvent) => {
            if ((event.target as HTMLElement)?.closest?.('[data-view-only]')) return;
            markSceneDirty();
        },
        [markSceneDirty]
    );

    const activeSceneEditorTool = activeTool ?? T._('Objects', 'Admin');

    useEffect(() => {
        if (!activeTool) setActiveTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, activeTool, setActiveTool]);

    // USCENDO DA LOOK LA PREVIEW SI SPEGNE (utente, 2026-10-20): sempre, non solo quando l'aveva accesa Look.
    // Look è il posto dove si guarda la scena come la vedrà il cliente; tornando a Objects o Materials si
    // torna alla vista di lavoro.
    const previousTool = useRef(activeSceneEditorTool);

    useEffect(() => {
        const leftLook = previousTool.current === T._('Look', 'Admin') && activeSceneEditorTool !== T._('Look', 'Admin');
        previousTool.current = activeSceneEditorTool;
        if (leftLook && backgroundOn) toggleBackground();
    }, [activeSceneEditorTool, backgroundOn, toggleBackground]);

    // Ref per la funzione di refresh della visibilità delle mesh
    const refreshMeshVisibilityRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (sceneEditor && zkScene) {
            sceneReadyRef.current = false;
            const meshes = sceneEditor.getSceneMeshes();
            const materials = sceneEditor.getAllMaterials();
            const lights = sceneEditor.getAllLights();

            // AGGIUNTO: Carica le camera locations dal zkScene
            const cameraLocations = zkScene.get('cameraLocations')?.models || [];

            handleMeshesUpdated(meshes);
            handleMaterialsUpdated(materials);
            handleCameraLocationsUpdated(cameraLocations);

            // GLI EFFETTI DELLA SCENA ARRIVANO ANCHE AI MATERIALI CARICATI (QA, 2026-10-12): tone mapping e
            // color grading toccavano solo lo sfondo, perché ogni materiale riletto dal salvataggio porta
            // una copia sua della configurazione dell'immagine. L'opzione è del motore e solo l'editor nuovo
            // la accende; un motore che non la conosce non fa niente.
            (sceneEditor as any).setFollowSceneImageProcessing?.(true);
            // IN PREVIEW SI CONTINUA A LAVORARE (Matteo, 2026-10-16): il clic seleziona, il passaggio evidenzia,
            // alone e gizmo restano sull'oggetto; della preview restano fondale, pavimento e antialiasing.
            // Anche questa è un'opzione del motore che accende solo l'editor nuovo.
            (sceneEditor as any).setEditingInViewer?.(true);

            setHasUnsavedChanges(false);
            sceneReadyRef.current = true;
            setIsSceneEditorReady(true);

            // Refresh mesh visibility dopo il caricamento iniziale
            setTimeout(() => {
                if (refreshMeshVisibilityRef.current) {
                    refreshMeshVisibilityRef.current();
                }
            }, 100);
            // L'impronta di partenza si prende quando la scena ha finito di sistemarsi da sé (visibilità, finiture).
            setTimeout(() => {
                savedFingerprintRef.current = sceneFingerprint(sceneEditor, zkScene);
            }, 1500);
        }
        return () => {
            sceneReadyRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneEditor, zkScene]);

    // Event handlers stabilizzati con useCallback
    const handleMeshesUpdated = useCallback(
        (meshes: any[]) => {
            markSceneDirty();

            setState((prevState) => ({
                ...prevState,
                meshes: (meshes ?? [])
                    .filter((mesh) => {
                        return mesh.name.toLowerCase() !== 'material_mesh';
                    })
                    .sort((a: any, b: any) => {
                        if (a.name < b.name) return -1;
                        if (a.name > b.name) return 1;
                        return 0;
                    })
            }));
        },
        [markSceneDirty]
    );

    const handleMaterialsUpdated = useCallback(
        (materials: any[]) => {
            markSceneDirty();
            // A VOLTE ARRIVANO I MATERIALI, A VOLTE I GRUPPI che li contengono: il motore manda i gruppi
            // (`getGroupedMaterials`), uno per cartella più quello dei materiali sciolti. Prenderne solo
            // il primo perdeva i materiali nelle cartelle, che non arrivavano a chi legge questo elenco
            // — gli slot degli oggetti fra loro. Il vecchio backoffice li concatenava tutti, e nascondeva
            // soltanto il ripiego di Babylon (`default material`), che non è un materiale della scena.
            const actualMaterials = (materials ?? []).flatMap((entry: any) => (Array.isArray(entry?.materials) ? entry.materials : [entry]));

            const filteredMaterials = actualMaterials
                .filter((material: any) => {
                    return material && material.id !== 'default material';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

            setState((prevState) => ({
                ...prevState,
                materials: filteredMaterials
            }));
        },
        [markSceneDirty]
    );

    // Aggiorna handleLightsUpdated per essere più completo
    const handleLightsUpdated = useCallback(
        (lights: any[]) => {
            markSceneDirty();
            const filteredLights = (lights ?? [])
                .filter((light) => {
                    // Filtra eventuali luci di sistema o default se necessario
                    return light.name && light.name.toLowerCase() !== 'default_light';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

            setState((prevState) => ({
                ...prevState,
                lights: filteredLights
            }));
        },
        [markSceneDirty]
    );

    const handleCameraLocationsUpdated = useCallback(
        (cameraLocations: any[]) => {
            markSceneDirty();
            setState((s) => ({ ...s, cameraLocations }));
        },
        [markSceneDirty]
    );

    // Callback per registrare la funzione di refresh delle mesh
    const handleRefreshMeshVisibility = useCallback((refreshFn: () => void) => {
        refreshMeshVisibilityRef.current = refreshFn;
    }, []);

    const handleSceneLoaded = useCallback((scene: BABYLON.Scene) => {
        void scene;
    }, []);

    const handleBeforeSceneLoaded = useCallback(async () => {
        return;
    }, []);

    const handleSceneTasksCheck = useCallback((...args: any[]) => {
        // If the first argument is the tasks array, update state accordingly
        const tasks = Array.isArray(args[0]) ? args[0] : args;
        setState((s) => ({ ...s, tasks }));
    }, []);

    // UN OGGETTO TOCCATO È UNA SCENA DA SALVARE, e basta (QA, 2026-10-15 — «Basic properties lente»).
    // Qui arrivava ogni numero del pannello, e ogni passo del trascinamento su un campo, e rifaceva
    // `state.meshes` con un `map` che non sostituiva niente: chi chiama passa l'id della riga, non una
    // mesh, quindi nessun `uniqueId` combaciava. L'array nuovo però ridisegnava la pagina intera — barra,
    // palco, statusbar, le altre sezioni — per un valore che nessuno legge: la mesh è la stessa, cambiata
    // al suo posto. Era metà del lavoro fra il numero battuto e l'oggetto mosso in scena; l'altra metà la
    // toglie `objectEditor`, che per le trasformate non riscrive più l'albero.
    const handleMeshUpdated = useCallback(() => markSceneDirty(), [markSceneDirty]);

    // GLI OGGETTI CAMBIATI DALLA LISTA SONO MODIFICHE (2026-10-15): spostati in un altro gruppo, raggruppati,
    // clonati, senza materiale. Chi le fa chiama questo per ridisegnare la scena, e finora la testata restava su
    // «Saved» — l'ascolto dei campi non le vede, perché non passano da un campo.
    const notifySceneEditorUpdates = useCallback(() => {
        if (!sceneEditor) return;

        sceneEditor.render();
        sceneEditor.updateBoundingBox();
        markSceneDirty();
    }, [sceneEditor, markSceneDirty]);

    // Funzione per il salvataggio usando l'hook
    const handleSave = useCallback(async () => {
        try {
            const productInfo = {
                name: productState.product.name,
                code: productState.product.code,
                environment: 2,
                // UN PRODOTTO DA ZERO VA SUL CANALE SCELTO NEL WIZARD, quello senza store: senza, il
                // salvataggio lo metterebbe sul canale 1. Quello dal catalogo resta dov'era.
                salesChannelID: productState.merchantProduct ? undefined : productState.salesChannelID || undefined
            };

            // ✅ NUOVO: Validazione AR PRIMA della notificazione di saving
            if (sceneEditor) {
                const { validateARBeforeSave } = await import('src/components/widgets/preview3D/hooks/useSaveProduct');
                await validateARBeforeSave(sceneEditor);
            }

            // Se il VTO è attivo, gestisci le impostazioni VTO prima del salvataggio
            if (sceneEditor && zkScene) {
                // Always hide all references on save so they are not visible and not
                // downloaded at start of customizer/DAM
                sceneEditor.setVTOReferenceVisible(false);

                // Verifica se il VTO è abilitato e applica le impostazioni
                const tryOnSettings = sceneEditor.getTryOnSettings();
                if (tryOnSettings && zkScene.getTryOnEnabled()) {
                    // Applica le impostazioni VTO correnti alla scena
                    sceneEditor.setTryOnTypeSettings(tryOnSettings);
                }

                // Imposta il nome della scena basato sul nome del prodotto
                zkScene.set('name', productInfo.name + ' Scene');
            }

            // Mostra notifica di progresso
            const notificationId = showInfoNotification({
                message: T._('Saving scene...', 'Admin')
            });

            // AL SALVATAGGIO SI RIEMPIONO I BUCHI. Un materiale senza fotografia resta un tondo
            // disegnato in libreria, e chi lo guarda non ha modo di sapere che basta chiederla: qui
            // si è già fermi ad aspettare, ed è il momento in cui uno scatto costa meno.
            //
            // SOLO QUELLE CHE MANCANO. Rifarle tutte vorrebbe dire minuti di scatti a ogni
            // salvataggio, e le altre sono già giuste. Le fotografie entrano nei metadata della
            // scena, quindi vanno prese PRIMA che la scena parta: dopo, sarebbero salvate la volta
            // dopo.
            // UN MATERIALE, UN FLUSSO. Chi ha provato tutte e due le sponde della finitura lascia sul
            // materiale quella scelta per ultima: le mappe dell'altra escono dagli slot e il core le
            // pota. Va fatto PRIMA delle fotografie, o lo scatto ritrarrebbe un materiale che non è
            // quello che si salva.
            commitFinishChoices(sceneEditor?.getAllMaterials?.() ?? []);

            const missingPreviews = materialsWithoutPreview(sceneEditor, state.materials);
            if (missingPreviews.length) await shootPreviews(sceneEditor, missingPreviews);

            await saveProductAndScene({
                onProgress: (progress) => {
                    void progress;
                },
                fromSceneEditor: true,
                // ✅ NUOVO: Passa i dati corretti del prodotto
                productInfo: productInfo
            });

            // IL SALVATAGGIO PUÒ SPEGNERE L'ALONE: per serializzare la scena il core butta lo strato
            // e lo ricrea vuoto (`getSceneJson`). Chi ha una mesh scelta la ritroverebbe senza.
            applySelectionHighlight(sceneEditor, selectionHighlight);
            savedFingerprintRef.current = sceneFingerprint(sceneEditor, zkScene);

            // Rimuovi notifica di progresso e mostra successo
            removeNotification(notificationId);
            setHasUnsavedChanges(false);
            unsavedChanges.resetSnapshot();
            showSuccessNotification({
                message: T._('Scene saved successfully', 'Admin')
            });
            return true;
        } catch (error) {
            console.error('Errore durante il salvataggio:', error);

            // Mostra notifica di errore
            showErrorNotification({
                message: T._('Failed to save the scene', 'Admin')
            });
            return false;
        }
    }, [saveProductAndScene, sceneEditor, selectionHighlight, zkScene, productState.product, state.materials, unsavedChanges]);

    // LE DUE LISTE SI SEGUONO. Ritrovare una cosa vuol dire due movimenti insieme: la colonna di
    // sinistra cambia sezione, e dentro quella sezione la riga giusta si accende — coi gruppi
    // chiusi aperti lungo la strada. Prima il primo movimento mangiava il secondo: cliccando una
    // mesh nel palco mentre eri sui materiali, la sezione passava agli oggetti e lì finiva, con
    // l'elenco aperto sulla riga sbagliata. Bisognava cliccare la stessa mesh una seconda volta.
    //
    // IL PANNELLO NUOVO NON C'È ANCORA quando si chiede il cambio: si monta in questo giro di
    // render, e il suo albero esiste dal giro dopo. Si dichiara quindi cosa si stava cercando, e
    // appena la sezione è quella giusta la riga si accende da sé.
    const [pendingReveal, setPendingReveal] = useState<{ tool: string; dataID: string } | null>(null);

    const refOfTool = useCallback((tool: string) => (tool === T._('Materials', 'Admin') ? materialsRef : treeRef), []);

    const revealTarget = useCallback(
        (tool: string, dataID: string) => {
            if (activeSceneEditorTool !== tool) {
                setActiveTool(tool);

                // NON SI SVUOTA NELL'ATTESA. Il soggetto nuovo arriva un giro dopo, quando l'albero
                // della sezione si è montato: svuotare adesso fa chiudere e riaprire la colonna per una
                // cosa che sta per essere sostituita.
                setPendingReveal({ tool, dataID });
                return;
            }

            refOfTool(tool).current?.selectAndOpenOverlay?.(dataID);
        },
        [activeSceneEditorTool, setActiveTool, refOfTool]
    );

    useEffect(() => {
        if (!pendingReveal || activeSceneEditorTool !== pendingReveal.tool) return;

        const { dataID, tool } = pendingReveal;
        setPendingReveal(null);
        // Un giro dopo: l'albero della sezione nuova si sta montando proprio adesso.
        const frame = requestAnimationFrame(() => refOfTool(tool).current?.selectAndOpenOverlay?.(dataID));
        return () => cancelAnimationFrame(frame);
    }, [pendingReveal, activeSceneEditorTool, refOfTool]);

    const handleMeshSelected = useCallback((meshID: any) => revealTarget(T._('Objects', 'Admin'), meshID), [revealTarget]);

    // «DOVE STA?» NON È «PORTAMI LÀ», e finora erano la stessa funzione. Cliccare una mesh nel palco
    // vuol dire «adesso guardo questa», e sceglierla è la risposta giusta. La freccetta dentro uno
    // Slot chiede un'altra cosa: QUALE sia e DOVE stia — e rispondere scegliendola butta via il
    // soggetto che si stava leggendo, aprendo il pannello del materiale proprio a chi stava
    // guardando l'oggetto che lo usa.
    //
    // La domanda quindi cambia sezione e ACCENDE la riga, e basta: è la stessa risposta che l'albero
    // dà già alla freccetta della sua testata.
    // `asRow`: chi chiede conosce la RIGA, non la cosa nel mondo 3D — è la freccetta della testata di un
    // pannello, che sa quale riga sta descrivendo. In un modello importato due mesh possono portare lo
    // stesso `id`, due righe no.
    const [whereRequest, setWhereRequest] = useState<{ tool: string; dataID: string; asRow?: boolean; token: number } | null>(null);

    // MOSTRARE UNA SEZIONE È UN GESTO SOLO, e lo chiedono in tre: il rail, le linguette della striscia
    // e la freccetta «dove sta». Vuol dire sceglierla e, se il suo pannello è chiuso, riaprirlo: una
    // voce premuta che lascia il pannello chiuso è un comando che non risponde. Quello che serve per
    // farlo si legge più sotto; la funzione vive in un riferimento riempito durante il disegno, ed è
    // questo a darle identità fissa.
    const showSectionRef = useRef<(tool: string) => void>(() => {});
    const showSection = useCallback((tool: string) => showSectionRef.current(tool), []);

    // «DOVE STA?» NON DIPENDE DALLA SEZIONE ATTIVA. Ne dipendeva, quindi rinasceva a ogni cambio di
    // sezione, e con lui i «dove sta» qui sotto, che la libreria tiene fra le dipendenze della sua
    // fabbrica di pannelli: la libreria rifaceva l'albero, e l'albero rimetteva nell'Inspector il
    // materiale aperto chissà quando. Da fuori: «cambio sezione e l'Inspector passa a un materiale».
    const askWhereItIs = useCallback(
        (tool: string, dataID: string, asRow?: boolean) => {
            showSection(tool);

            // IL GETTONE SERVE A CHIEDERE DUE VOLTE LA STESSA RIGA: senza, la seconda domanda sarebbe
            // lo stesso valore di prima e non succederebbe niente.
            setWhereRequest((previous) => ({ tool, dataID, asRow, token: (previous?.token ?? 0) + 1 }));
        },
        [showSection]
    );

    // A UNA DOMANDA SI RISPONDE UNA VOLTA: chi la riceve la gira al suo albero e lo dice. Senza, ogni
    // sezione che rinasceva la ritrovava, e la riga di allora rilampeggiava riaprendo i suoi rami.
    const consumeWhereRequest = useCallback((token: number) => setWhereRequest((current) => (current?.token === token ? null : current)), []);

    // I «DOVE STA» SONO FUNZIONI STABILI, e non è pignoleria: scritti in linea dentro il JSX
    // nascono nuovi a ogni ridisegno di questa pagina, e una sezione che li mette fra le dipendenze
    // di una `useCallback` si ritrova a rifare tutto — la fabbrica dei suoi pannelli, il suo albero
    // di nodi — a ogni giro. Costa poco scriverli qui e vale per chiunque li riceva: è l'invariante
    // che rende innocuo dipenderne.
    const revealMaterial = useCallback((materialID: string) => askWhereItIs(T._('Materials', 'Admin'), materialID), [askWhereItIs]);
    const revealObject = useCallback((meshID: string, asRow?: boolean) => askWhereItIs(T._('Objects', 'Admin'), meshID, asRow), [askWhereItIs]);
    const revealLight = useCallback((lightID: string, asRow?: boolean) => askWhereItIs(T._('Lights', 'Admin'), lightID, asRow), [askWhereItIs]);
    const revealCamera = useCallback((cameraID: string, asRow?: boolean) => askWhereItIs(T._('Cameras', 'Admin'), cameraID, asRow), [askWhereItIs]);

    // APRIRE UN MATERIALE è il doppio clic su uno slot, ed è un gesto diverso da «dove sta»: la libreria
    // lo SCEGLIE e l'Inspector passa a lui. La libreria è sempre montata — trasloca, non rinasce —
    // quindi la si mostra dov'è e le si chiede la riga appena il suo nodo è arrivato al suo posto.
    const openMaterial = useCallback(
        (materialID: string) => {
            showSection(T._('Materials', 'Admin'));
            requestAnimationFrame(() => materialsRef.current?.selectAndOpenOverlay?.(materialID));
        },
        [showSection]
    );
    // Lo slot del materiale può anche aprire l'oggetto associato selezionandolo nell'albero.
    const revealTargetRef = useRef(revealTarget);
    revealTargetRef.current = revealTarget;
    const openObject = useCallback((meshID: string) => revealTargetRef.current(T._('Objects', 'Admin'), meshID), []);

    // I DATI VIVI PASSANO DA UN RIFERIMENTO, e non dalla chiusura delle fabbriche qui sotto: è la
    // condizione che permette a quelle di avere identità fissa. Si riempie durante il disegno,
    // subito prima che le sezioni lo leggano.
    const live = useRef<any>({});

    live.current = {
        state,
        sceneEditor,
        zkScene,
        additionalFeatures,
        handleMeshUpdated,
        notifySceneEditorUpdates,
        handleMeshesUpdated,
        handleMaterialsUpdated,
        handleLightsUpdated,
        handleCameraLocationsUpdated,
        handleRefreshMeshVisibility,
        revealMaterial,
        openMaterial,
        revealObject,
        revealLight,
        revealCamera,
        openObject,
        whereRequest,
        consumeWhereRequest,
        markSceneDirty,
        // APRIRE ENVIRONMENT ACCENDE IL FONDALE (utente, 2026-09-22): regola environment, fondale e
        // pavimento, cioè le tre cose che nel modo Editor NON si disegnano. Uscendo da Look si spegne.
        turnBackgroundOn: () => {
            if (!backgroundOn) toggleBackground();
        }
    };

    // LE FABBRICHE DELLE SEZIONI HANNO IDENTITÀ FISSA, e non è un'ottimizzazione. La barra a
    // sinistra rende `component` come TIPO di componente — `<ToolComponent/>` — e per React un tipo
    // diverso è un componente diverso: smonta quello di prima e ne monta uno nuovo da zero. Scritte
    // in linea dentro il memo qui sotto, queste funzioni rinascevano a ogni cambio di `state.lights`
    // o `state.materials`, cioè a ogni modifica della scena.
    //
    // COSA COSTAVA: la sezione aperta perdeva tutto — il suo elenco, la selezione, la riga aperta —
    // e siccome nessuno aveva più una riga aperta, nessuno rifaceva il pannello: la colonna di
    // destra restava a disegnare l'ultima cosa che le era stata consegnata. Da fuori era «rinomino
    // una luce e il pannello mostra il nome vecchio». Il cambio di specie non si riprendeva mai,
    // perché a morire era anche il ricordo della luce appena nata che serviva a riaprirla.
    const ObjectsTool = useCallback(
        (props: any) => (
            <ObjectEditor
                materials={live.current.state.materials}
                onOpenMaterial={live.current.openMaterial}
                onMeshUpdate={live.current.handleMeshUpdated}
                sceneEditor={live.current.sceneEditor}
                notifySceneEditorUpdates={live.current.notifySceneEditorUpdates}
                onMeshesUpdated={live.current.handleMeshesUpdated}
                onRevealMaterial={live.current.revealMaterial}
                onRevealObject={live.current.revealObject}
                onRefreshMeshVisibility={live.current.handleRefreshMeshVisibility}
                whereRequest={live.current.whereRequest?.tool === T._('Objects', 'Admin') ? live.current.whereRequest : null}
                onWhereServed={live.current.consumeWhereRequest}
                {...props}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const LightsTool = useCallback(
        (props: any) => (
            <LightsEditor
                {...props}
                lights={live.current.state.lights}
                selectedLights={live.current.state.selectedLights}
                sceneEditor={live.current.sceneEditor}
                onLightsUpdated={live.current.handleLightsUpdated}
                onRevealLight={live.current.revealLight}
                whereRequest={live.current.whereRequest?.tool === T._('Lights', 'Admin') ? live.current.whereRequest : null}
                onWhereServed={live.current.consumeWhereRequest}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const CamerasTool = useCallback(
        (props: any) => (
            <CameraEditor
                {...props}
                cameraLocations={live.current.state.cameraLocations}
                selectedCameraLocations={live.current.state.selectedCameraLocations}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                onCameraLocationsUpdated={live.current.handleCameraLocationsUpdated}
                onRevealCamera={live.current.revealCamera}
                whereRequest={live.current.whereRequest?.tool === T._('Cameras', 'Admin') ? live.current.whereRequest : null}
                onWhereServed={live.current.consumeWhereRequest}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const MaterialsTool = useCallback(
        (props: any) => (
            <MaterialsEditor
                {...props}
                materials={live.current.state.materials}
                selectedMaterials={live.current.state.selectedMaterials}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                onMaterialsUpdated={live.current.handleMaterialsUpdated}
                onRevealMaterial={live.current.revealMaterial}
                onOpenObject={live.current.openObject}
                onSceneChanged={live.current.markSceneDirty}
                whereRequest={live.current.whereRequest?.tool === T._('Materials', 'Admin') ? live.current.whereRequest : null}
                onWhereServed={live.current.consumeWhereRequest}
                treeRef={materialsRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const TexturesTool = useCallback(() => <TexturesEditor />, []);

    const EnvironmentsTool = useCallback(() => <EnvironmentsEditor />, []);

    const LookTool = useCallback(
        (props: any) => (
            <LookEditor
                {...props}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                additionalFeatures={live.current.additionalFeatures}
                onSceneChanged={() => live.current.markSceneDirty()}
                onEnvironmentOpen={() => live.current.turnBackgroundOn()}
            />
        ),
        []
    );

    // LE VOCI DEL PROTOTIPO V2 (round 4, prototipo #25): Objects · Materials · Lights · Cameras | Look,
    // uguali nei due attracchi. Sopra ciò che sta NELLA scena e la libreria da cui pesca; sotto il
    // separatore ciò che le sta INTORNO. «Cameras» resta il nome del backoffice (non «Shots»).
    const declaredSceneEditorTools: StepsTools[] = useMemo(
        () => [
            {
                tool: T._('Objects', 'Admin'),
                icon: <ObjectsIcon />,
                tooltip: T._('Objects', 'Admin'),
                onClick: () => {},
                component: ObjectsTool
            },
            {
                tool: T._('Materials', 'Admin'),
                icon: <MaterialsIcon />,
                tooltip: T._('Materials', 'Admin'),
                onClick: () => {},
                component: MaterialsTool
            },
            {
                tool: T._('Lights', 'Admin'),
                icon: <LightsIcon />,
                tooltip: T._('Lights', 'Admin'),
                onClick: () => {},
                component: LightsTool
            },
            {
                tool: T._('Cameras', 'Admin'),
                icon: <CamerasIcon />,
                tooltip: T._('Cameras', 'Admin'),
                onClick: () => {},
                component: CamerasTool
            },
            {
                tool: T._('Textures', 'Admin'),
                icon: <TexturesIcon />,
                tooltip: T._('Textures', 'Admin'),
                onClick: () => {},
                component: TexturesTool
            },
            {
                tool: T._('Env', 'Admin'),
                icon: <EnvironmentsIcon />,
                tooltip: T._('Environments', 'Admin'),
                onClick: () => {},
                component: EnvironmentsTool
            },
            {
                tool: T._('Look', 'Admin'),
                icon: <LookIcon />,
                tooltip: T._('Environment and post-process', 'Admin'),
                onClick: () => {},
                separatorBefore: true,
                component: LookTool
            }
        ],
        [ObjectsTool, MaterialsTool, LightsTool, CamerasTool, TexturesTool, EnvironmentsTool, LookTool]
    );

    // LE SEZIONI ANCORA VUOTE: trigger point, texture e ambienti hanno un editor che è solo un
    // guscio, perché i dati dietro non esistono ancora (docs/scene-editor-new-sections.md). Le voci
    // restano dichiarate qui sopra e, finché `SHOW_NOT_YET_AVAILABLE` è spento, non si mostrano nel
    // rail. Una sezione che si riempie esce da questo elenco, e riprende posto, glifo e ordine di prima.
    const deferredToolNames = useMemo<string[]>(() => (SHOW_NOT_YET_AVAILABLE ? [] : [T._('Textures', 'Admin'), T._('Env', 'Admin')]), []);

    const sceneEditorTools = useMemo(
        () => declaredSceneEditorTools.filter((stepTool) => !deferredToolNames.includes(stepTool.tool)),
        [declaredSceneEditorTools, deferredToolNames]
    );

    // L'INSPECTOR HA UN PADRONE ALLA VOLTA, e chi gli scrive sta in due posti: il rail (oggetti, luci,
    // camere) e la libreria, che non si smonta mai perché trasloca. La libreria ricordava la sua riga
    // aperta e la rimetteva nella colonna a ogni cambiamento dei propri dati — anche a colonna ormai di
    // un altro: si sceglieva un oggetto, si cambiava sezione, e tornava un materiale aperto chissà quando.
    //
    // Chi prende la colonna la toglie all'altro, che dimentica la sua riga aperta senza scrivere niente;
    // il vuoto lo può scrivere solo chi aveva scritto.
    const inspectorOwner = useRef<'rail' | 'library' | null>(null);

    const publishInspector = useCallback((owner: 'rail' | 'library', content: React.ReactNode | null) => {
        if (content === null) {
            if (inspectorOwner.current !== owner) return;
            inspectorOwner.current = null;
            setInspectorContent(null);
            return;
        }

        if (inspectorOwner.current && inspectorOwner.current !== owner) (owner === 'rail' ? materialsRef : treeRef).current?.releaseOverlay?.();
        inspectorOwner.current = owner;
        setInspectorContent(content);
    }, []);

    const publishFromRail = useCallback((content: React.ReactNode | null) => publishInspector('rail', content), [publishInspector]);

    // Chiudere il pannello lo chiude per tutte: non si sa da quale sezione era stato aperto, e una
    // sezione che resta convinta di avere un pannello aperto lo riapre al primo aggiornamento.
    const closeOverlay = useCallback(() => {
        inspectorOwner.current = null;
        setInspectorContent(null);
        treeRef.current?.closeOverlay();
        materialsRef.current?.closeOverlay();
    }, []);

    // Lo store del motore, non il suo contenuto: serve a riportarlo dentro le finestre, che si
    // disegnano fuori da questo albero di componenti.
    const preview3DStore = useContext(Preview3DStoreContext);

    const openSettings = useCallback(
        (initialCategory?: SceneSettingsPage) => {
            // UN CLIC È GIÀ LA SCELTA, E LA X CHIUDE E BASTA (prototipo V3; utente, 2026-10-19): niente Confirm e
            // niente annulla. Le preferenze si salvano subito per chi le sceglie; le voci di scena segnano la
            // scena da salvare, e Save le porta al server. Un annulla vero non c'è: i file del VTO e il QR dell'AR
            // partono subito, e la X che fingeva di annullare rimetteva solo le preferenze.
            showDialog(
                'scene-editor-settings',
                // LA FINESTRA ESCE DAL PANNELLO, e con lui dal contesto del motore 3D: le finestre di questo
                // backoffice si disegnano in un portale attaccato alla radice della pagina, quindi tutto ciò
                // che sta dentro si ritrova FUORI dal provider che tiene scena, editor e prodotto. Il VTO lo
                // chiede al primo respiro e cadeva con «usePreview3DStore must be used within a
                // Preview3DStoreContext.Provider» — cioè la pagina intera andava in errore aprendo una
                // sezione delle impostazioni.
                //
                // Si riporta dentro lo stesso store, non una copia: è il gesto che il DAM e il caricamento
                // dei materiali fanno già quando aprono una finestra loro.
                <Preview3DStoreContext.Provider value={preview3DStore}>
                    <Dialog title={T._('Settings', 'Admin')} windowDecorator={SettingsDialogWindow} onClose={() => closeDialog('scene-editor-settings')}>
                        <SceneEditorSettingsDialog
                            host='sceneEditor'
                            initialCategory={initialCategory}
                            preferences={preferences}
                            setPreference={setPreference}
                            selectionHighlight={selectionHighlight}
                            onSelectionHighlightChange={updateSelectionHighlight}
                            onResetPanelSizes={resetPanelSizes}
                            sceneEditor={sceneEditor}
                            zkScene={zkScene}
                            zkProduct={zkProduct}
                            additionalFeatures={additionalFeatures}
                            onSceneChanged={markSceneDirty}
                        />
                    </Dialog>
                </Preview3DStoreContext.Provider>
            );
        },
        [
            additionalFeatures,
            closeDialog,
            markSceneDirty,
            preferences,
            preview3DStore,
            resetPanelSizes,
            sceneEditor,
            selectionHighlight,
            setPreference,
            showDialog,
            updateSelectionHighlight,
            zkProduct,
            zkScene
        ]
    );

    // L'INGRANAGGIO DI UNA SECTION (Behavior dell'oggetto) apre i Settings sulla sua pagina.
    useSceneSettingsRequest((page) => openSettings(page));

    // LA LIBRERIA È UNA SEZIONE DEL RAIL COME LE ALTRE, ma il suo nodo vive per conto suo (portale)
    // e si trasloca nella colonna quando tocca a lei: così selezione, filtro e anteprime in corso
    // sopravvivono a un giro su Objects (docs/scene-editor-step6-second-layout.md § 1). Le sezioni
    // rimandate al secondo round mancano anche qui.
    const libraryToolNames = useMemo(
        () => [T._('Materials', 'Admin'), T._('Textures', 'Admin'), T._('Env', 'Admin')].filter((tool) => !deferredToolNames.includes(tool)),
        [deferredToolNames]
    );

    const [libraryTool, setLibraryTool] = useState(() => libraryToolNames[0]);
    const activeLibraryTool = libraryToolNames.includes(activeSceneEditorTool) ? activeSceneEditorTool : libraryTool;
    const librarySection = useMemo(() => sceneEditorTools.find((stepTool) => stepTool.tool === activeLibraryTool), [sceneEditorTools, activeLibraryTool]);

    useEffect(() => {
        if (libraryToolNames.includes(activeSceneEditorTool)) setLibraryTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, libraryToolNames]);

    showSectionRef.current = (tool) => {
        if (activeSceneEditorTool !== tool) setActiveTool(tool);
        // UNA VOCE PREMUTA A PANNELLO CHIUSO LO RIAPRE (QA, 2026-10-12, W5).
        if (preferences.collapsed.includes('left')) toggleCollapsed('left');
    };

    // LA LIBRERIA SCRIVE NELL'INSPECTOR SOLO QUANDO È LEI LA SEZIONE APERTA. Resta montata anche dietro
    // le altre, e il suo albero ripubblica il pannello del materiale aperto ogni volta che i nodi
    // cambiano: entrando in Look, il fondale che si accende cambiava i materiali e il materiale si
    // riprendeva la colonna che Look aveva appena dato a Environment.
    const activeToolRef = useRef(activeSceneEditorTool);
    activeToolRef.current = activeSceneEditorTool;
    //
    // E PASSA DAL PADRONE UNICO DELL'INSPECTOR (`publishInspector`): prendere la colonna la toglie al rail.
    const publishFromLibrary = useCallback(
        (content: React.ReactNode | null) => {
            if (content !== null && !libraryToolNames.includes(activeToolRef.current)) return;
            publishInspector('library', content);
        },
        [libraryToolNames, publishInspector]
    );

    const railContentRef = useRef<HTMLDivElement | null>(null);
    // La presa del secondo pannello si misura sul pannello stesso, non sul palco: vedi `side` in
    // `workspaceLayout`.
    const inspectorRef = useRef<HTMLDivElement | null>(null);
    const { hostRef: libraryHostRef, moveTo: moveLibrary } = useMovableHost();

    const railSelectedTool = sceneEditorTools.some((stepTool) => stepTool.tool === activeSceneEditorTool) ? activeSceneEditorTool : sceneEditorTools[0]?.tool;

    // Il trasloco: il nodo entra nel contenitore del rail solo quando la sezione scelta è della
    // libreria; se no lì c'è un'altra sezione e i due si affiancherebbero.
    useLayoutEffect(() => {
        moveLibrary(libraryToolNames.includes(activeSceneEditorTool) ? railContentRef.current : null);
    }, [moveLibrary, activeSceneEditorTool, libraryToolNames, preferences.collapsed]);

    // LA COLONNA DELL'INSPECTOR C'È SEMPRE, in tutt'e due gli attracchi (prototipo #28, #64; collega,
    // 2026-09-09: «il pannello destro non chiuderlo mai: sostituisci il contenuto interno»). Senza un
    // soggetto resta al suo posto e lo dice; farla sparire a ogni clic a vuoto rifaceva la griglia due
    // volte. Chi la vuole stretta la chiude dal piede, e da chiusa resta una striscia col chevron.
    const showInspectorPanel = preferences.layoutMode === 'workspace';
    const isInspectorCollapsed = preferences.collapsed.includes('right');
    const isInspectorOpen = showInspectorPanel && !isInspectorCollapsed;
    const collapsedPanels = preferences.collapsed.join(' ') || undefined;

    // Scegliere una cosa RIAPRE LA COLONNA, se era chiusa. La chiusura vive nelle preferenze e
    // sopravvive al ricaricamento: senza questo, un solo clic sul chevron rendeva muto ogni clic
    // successivo su una riga — il soggetto cambiava davvero, ma la colonna restava una striscia e non
    // lo diceva nessuno. A tenerla stretta resta il gesto di chiuderla, che si rifà in un clic.
    //
    // LO STATO LO LEGGE UN RIFERIMENTO, non le dipendenze: con `preferences.collapsed` fra le
    // dipendenze, chiudere la colonna di SINISTRA rieseguirebbe questo effetto.
    const revealInspector = useRef<() => void>(() => {});
    revealInspector.current = () => {
        if (preferences.collapsed.includes('right')) toggleCollapsed('right');
    };

    useEffect(() => {
        if (inspectorContent) revealInspector.current();
    }, [inspectorContent]);
    const workspaceStyle = {
        '--w-left': `${preferences.sizes.left}px`,
        '--w-right': `${preferences.sizes.right}px`,
        '--w-rail-cur': `${getWorkspaceRailWidth(preferences.sectionRails)}px`
    } as React.CSSProperties;

    return (
        // L'ATTESA È LA STESSA DEL COMPOSER, card compresa: sono due ambienti della stessa
        // piattaforma, e prima che compaiano non c'è altro da guardare che questa. Il velo nudo di
        // prima mostrava i pallini senza dire che cosa stesse caricando — su una scena 3D, che ci
        // mette secondi veri, si leggeva come una pagina piantata.
        <Load3DSceneEditor
            onProgress={() => null}
            fallback={
                <Container>
                    <EditorLoadingCard
                        title={T._('Loading Scene Editor', 'Admin')}
                        lines={[T._('We are loading the 3D scene and its resources. This may take a few moments.', 'Admin')]}
                    />
                </Container>
            }
            damMode={false}
            isConfiguratorProduct={true}
            notLoadDesign={true}
        >
            <Container>
                {/* Non disegna niente: dice al motore chi è il provider VTO di questo prodotto. */}
                <VTOProviderSync productModelID={zkProduct?.get?.('modelID')} />
                <HeaderToolbar
                    closeOverlay={closeOverlay}
                    hasUnsavedChanges={hasUnsavedChanges}
                    checkUnsavedChanges={checkUnsavedSceneChanges}
                    onSave={handleSave}
                    isSaving={isSaving}
                    onSettings={() => openSettings()}
                    activeMode='scene'
                    /* Un prodotto nato da zero non ha ancora una fotografia da cui misurare le modifiche:
                       un nome nuovo lo si dice da qui. */
                    onProductRenamed={markSceneDirty}
                />
                {/* CHI ASCOLTA LE MODIFICHE STA SOPRA IL PALCO, non sul palco. La libreria, che
                    trasloca, si vede dentro il Workspace ma nell'albero di React è qui fuori,
                    appesa al portale in fondo: un ascolto
                    messo sul Workspace non le sentiva, e una texture cambiata dalla libreria
                    lasciava la scena pulita quando invece era sporca. */}
                <MainContentContainer
                    onChangeCapture={sceneMayHaveChanged}
                    onInputCapture={sceneMayHaveChanged}
                    onPointerUpCapture={scheduleSceneCheck}
                    onKeyUpCapture={scheduleSceneCheck}
                >
                    <Workspace
                        ref={measureWorkspace}
                        className='workspace'
                        data-layout={preferences.layoutMode}
                        data-dock={preferences.propertiesPanel}
                        data-inspector={showInspectorPanel ? 'on' : 'off'}
                        data-collapse={collapsedPanels}
                        style={workspaceStyle}
                    >
                        <Panel data-panel='left'>
                            <LeftVerticalToolbar
                                tools={sceneEditorTools}
                                selectedTool={railSelectedTool}
                                /* CAMBIARE SEZIONE NON CHIUDE IL PANNELLO. La colonna di destra mostra il
                                   SOGGETTO, e cambiare la lista da cui si sceglie non è dire che non si sta
                                   più guardando niente: era un pannello che si chiudeva da solo mentre
                                   navighi, portandosi via quello che stavi tenendo d'occhio.

                                   E siccome la colonna esce dalla griglia quando non ha contenuto, quel
                                   vuoto non era nemmeno un pannello vuoto: era la colonna che spariva e
                                   ricompariva, spostando tutto il resto due volte.

                                   Il soggetto lo cambia chi ne sceglie un altro, o chi chiude.

                                   E una voce premuta a pannello chiuso lo riapre (`showSection`). */
                                onSelectTool={showSection}
                                onClosePanel={() => toggleCollapsed('left')}
                                collapsed={preferences.collapsed.includes('left')}
                                sectionRails={preferences.sectionRails}
                                onInspectorChange={publishFromRail}
                                hostedTools={libraryToolNames}
                                contentRef={railContentRef}
                            />
                        </Panel>
                        {/* LA PRESA STA FUORI DAL PANNELLO, non dentro. Dentro veniva tagliata dal suo
                            `overflow: hidden` e si misurava sul workspace invece che sul pannello: c'era,
                            ma non si poteva né vedere né afferrare. Da qui si posiziona da sé sulla
                            cucitura fra la prima colonna e ciò che le sta accanto.
                            E non c'è quando il pannello è chiuso: un confine si tira solo se ha due lati
                            da spartire. */}
                        {!preferences.collapsed.includes('left') && (
                            <Resizer
                                direction='left'
                                value={preferences.sizes.left}
                                min={limits.left.min}
                                max={limits.left.max}
                                defaultValue={defaults.left}
                                workspaceRef={workspaceRef}
                                cssVariable='--w-left'
                                onCommit={(value) => setPanelSize('left', value)}
                            />
                        )}
                        <StageColumn ref={stageRef} data-panel='stage'>
                            <PreviewContainer>
                                <SmallScreenBanner>
                                    <NotificationBanner type='INFO' variant='slim' content={T._('The side panels are hidden on small screens.', 'Admin')} />
                                </SmallScreenBanner>
                                {zkScene && zkProduct && sceneEditor && (
                                    <>
                                        <SceneEditorView
                                            variantId={zkProduct.get('colors').at(0).get('colorID')}
                                            isHighlightActive={true}
                                            selectedMeshId={null}
                                            isSelectionMode={true}
                                            onSelectionChanged={handleMeshSelected}
                                            onMeshesUpdated={handleMeshesUpdated}
                                            onMaterialsUpdated={handleMaterialsUpdated}
                                            onLightsUpdated={handleLightsUpdated}
                                            onCameraLocationsUpdated={handleCameraLocationsUpdated}
                                            onSceneTasksCheck={handleSceneTasksCheck}
                                        />
                                        {isSceneEditorReady && (
                                            <SceneEditorToolbar
                                                sceneEditor={sceneEditor}
                                                stageRef={stageRef}
                                                previewActive={backgroundOn}
                                                onPreviewToggle={toggleBackground}
                                                measurementUnit={preferences.measurementUnit}
                                                onMeasurementUnitChange={(unit) => setPreference('measurementUnit', unit)}
                                                selectionHighlight={selectionHighlight}
                                            />
                                        )}
                                    </>
                                )}
                                {!sceneEditor && <StageEmptyState>{T._('Loading scene...', 'Admin')}</StageEmptyState>}
                            </PreviewContainer>
                            {/* Il manico del divisore vive dove vive la colonna che allarga: senza
                                Inspector aperto tirerebbe una traccia che non c'è. */}
                            {preferences.propertiesPanel === 'right' && isInspectorOpen && (
                                <Resizer
                                    direction='right'
                                    value={preferences.sizes.right}
                                    min={limits.right.min}
                                    max={limits.right.max}
                                    defaultValue={defaults.right}
                                    workspaceRef={workspaceRef}
                                    referenceRef={stageRef}
                                    cssVariable='--w-right'
                                    onCommit={(value) => setPanelSize('right', value)}
                                />
                            )}
                        </StageColumn>
                        {/* ANCHE IL SECONDO PANNELLO SI ALLARGA. Il primo aveva la sua presa dal principio, il
                            secondo no: nell'attracco `right` la cucitura è fra palco e Inspector e la presa vive
                            là, ma qui l'Inspector sta IN MEZZO — fra le sezioni e il palco — e quella cucitura non
                            aveva nessuno. Due colonne larghe uguali e nessun modo di dare aria a quella che in quel
                            momento stai leggendo. */}
                        {preferences.propertiesPanel === 'side' && isInspectorOpen && (
                            <Resizer
                                direction='side'
                                value={preferences.sizes.right}
                                min={limits.right.min}
                                max={limits.right.max}
                                defaultValue={defaults.right}
                                workspaceRef={workspaceRef}
                                referenceRef={inspectorRef}
                                cssVariable='--w-right'
                                onCommit={(value) => setPanelSize('right', value)}
                            />
                        )}
                        {showInspectorPanel && (
                            <InspectorPanel ref={inspectorRef} data-panel='right'>
                                {/* La testata è del SOGGETTO, non della colonna: la disegna `InspectorShell`
                                    insieme a nome e specifiche. Chiudere la colonna è un gesto della
                                    colonna, e sta nel suo piede. */}
                                <InspectorBody hidden={isInspectorCollapsed}>
                                    <InspectorContent>
                                        {inspectorContent ?? (
                                            <>
                                                <InspectorEmptyHead />
                                                <InspectorEmpty>
                                                    <SelectedIcon aria-hidden='true' />
                                                    <p>{T._('Select something in a list or in the model to see its properties.', 'Admin')}</p>
                                                </InspectorEmpty>
                                            </>
                                        )}
                                    </InspectorContent>
                                </InspectorBody>
                                <InspectorFoot $side={preferences.propertiesPanel === 'side' ? 'left' : 'right'}>
                                    <div>
                                        <PanelCollapseToggle
                                            collapsed={isInspectorCollapsed}
                                            side={preferences.propertiesPanel === 'side' ? 'left' : 'right'}
                                            onToggle={() => toggleCollapsed('right')}
                                        />
                                    </div>
                                </InspectorFoot>
                            </InspectorPanel>
                        )}
                    </Workspace>
                    {/* La libreria vive qui, ferma nell'albero di React, e si vede dentro il nodo che
                        trasloca nella colonna. */}
                    {libraryHostRef.current &&
                        librarySection &&
                        createPortal(<librarySection.component onInspectorChange={publishFromLibrary} />, libraryHostRef.current)}
                </MainContentContainer>
            </Container>
        </Load3DSceneEditor>
    );
};

export default withEditorTheme(SceneEditor);
